import { pdpSectionStyle } from 'common/components/page-section';
import { Layout, RegularButton, Typography } from 'fronton-react';
import React from 'react';
import { jc, jcm } from 'utils';
import { useTranslation } from 'react-i18next';
import { FormSubmitNotification } from './content-blocks/form-submit-notification';
import { ReviewAuthorDetails } from './content-blocks/review-author-details';
import { ReviewScoresFormPart } from './content-blocks/review-scores-part';
import { ReviewTextFormPart } from './content-blocks/review-text-part';
import { UploadPhotosPart } from './content-blocks/upload-photo-part';
import { Styles } from './styles';
import { ReviewFormFieldError, ReviewFormProps } from './types';
import { PdpReviewFormBreadcrumbs } from './components';
import { PersonalDataAgreement } from './review-form-statefull/personal-data-agreement';

export const ReviewFormSection: React.FC<
  ReviewFormProps & {
    fieldsErrors: ReviewFormFieldError[] | null;
    canDoSubmit: boolean;
    toShowErrors: boolean;
    hasImageUploadError?: boolean;
  }
> = ({
  breakpoint,
  fieldsErrors,
  canDoSubmit,
  toShowErrors,
  selectedPictures,
  onPictureRetryClick,
  onPictureRemoveClick,
  onPictureSelectionClick,
  onPicturesFilesSelect,
  onSubmit,
  hasImageUploadError,
  ...props
}) => {
  const { t } = useTranslation();
  const isMobileView = breakpoint === 'mobile' || breakpoint === 'tablet';

  return (
    <Layout className={Styles.reviewFormWrapper}>
      <PdpReviewFormBreadcrumbs />
      <section className={jc(pdpSectionStyle, Styles.mainSection, props.className ?? '')}>
        <Typography
          variant="h1"
          as="h1"
          className={jcm({
            [Styles.sectionTitle]: true,
            [Styles.sectionTitleAtMobile]: isMobileView,
          })}
        >
          {t('reviews.form.title')}
        </Typography>
        <div
          className={jcm({
            [Styles.formLayout]: true,
            [Styles.formLayoutAtMobile]: isMobileView,
          })}
        >
          <div className={Styles.layoutMain}>
            <form onSubmit={onSubmit}>
              <ReviewScoresFormPart
                breakpoint={breakpoint}
                totalScore={props.totalScore}
                setTotalScore={props.setTotalScore}
                errors={toShowErrors ? fieldsErrors : null}
              />

              <ReviewTextFormPart
                breakpoint={breakpoint}
                body={props.body}
                pros={props.pros}
                cons={props.cons}
                setReviewBodyText={props.setReviewBodyText}
                setReviewProsText={props.setReviewProsText}
                setReviewConsText={props.setReviewConsText}
                onBodyFieldFocus={props.onBodyFieldFocus}
                onProsFieldFocus={props.onProsFieldFocus}
                onConsFieldFocus={props.onConsFieldFocus}
                errors={toShowErrors ? fieldsErrors : null}
              />

              <UploadPhotosPart
                breakpoint={breakpoint}
                picCovers={selectedPictures}
                onRetryClick={onPictureRetryClick}
                onRemoveClick={onPictureRemoveClick}
                onFileSelectionClick={onPictureSelectionClick}
                onFilesSelect={onPicturesFilesSelect}
                hasError={hasImageUploadError}
              />

              <ReviewAuthorDetails
                breakpoint={breakpoint}
                timeUsage={props.timeUsage}
                shop={props.shop}
                region={props.region}
                authorName={props.authorName}
                authorEmail={props.authorEmail}
                availabledStores={props.availabledStores ?? []}
                availabledRegions={props.availabledRegions ?? []}
                setTimeUsage={props.setTimeUsage}
                setShop={props.setShop}
                setAuthorName={props.setAuthorName}
                setAuthorEmail={props.setAuthorEmail}
                onAuthorNameFieldFocus={props.onAuthorNameFieldFocus}
                onAuthorEmailFieldFocus={props.onAuthorEmailFieldFocus}
                errors={toShowErrors ? fieldsErrors : null}
              />

              {!props.submitIsPerforming && (
                <>
                  <RegularButton
                    data-qa="submit-review"
                    type="submit"
                    size="l"
                    variant="primary"
                    disabled={!canDoSubmit}
                    className={jcm({
                      [Styles.submitButton]: true,
                      [Styles.submitButtonAtMobile]: isMobileView,
                    })}
                  >
                    {t('reviews.form.ui.submit')}
                  </RegularButton>

                  <PersonalDataAgreement
                    personalLinkHref="/pravila-publikacii-otzyvov/"
                    reviewRulesHref="/politika-obrabotki-personalnykh-dannykh/"
                  />
                </>
              )}

              {props.submitIsPerforming && <FormSubmitNotification />}
            </form>
          </div>
          <div
            className={jcm({
              [Styles.layoutSide]: true,
              [Styles.layoutSideAtMobile]: isMobileView,
            })}
          >
            {props.productTile}
          </div>
        </div>
      </section>
    </Layout>
  );
};
